import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Card, PageBody, PageHeader, StatusPill } from "../components/ui";
import { api } from "../lib/api";
import { sipStatusLabel } from "../lib/sipLabels";
import { useStore } from "../lib/store";
import type { AdminOverview } from "../lib/types";

function light(ok: boolean | undefined): "ok" | "fail" | "mute" {
  if (ok === undefined) return "mute";
  return ok ? "ok" : "fail";
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "ok" | "fail" | "live" | "mute" | "teal";
}) {
  return (
    <Card className="px-5 py-4">
      <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">{label}</div>
      <div className="mt-3">
        <StatusPill tone={tone ?? "mute"}>{value}</StatusPill>
      </div>
    </Card>
  );
}

/**
 * System overview. Never shows secret values — only configured flags.
 */
export function Admin() {
  const navigate = useNavigate();
  const { state, logout } = useStore();
  const [overview, setOverview] = useState<AdminOverview>({});
  const [leads, setLeads] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [adminResult, healthResult, settingsResult, leadsResult] = await Promise.allSettled([
        api.adminOverview(),
        api.health(),
        api.settings(),
        api.leads(),
      ]);
      if (cancelled) return;
      const health = healthResult.status === "fulfilled" ? healthResult.value : null;
      const settings = settingsResult.status === "fulfilled" ? settingsResult.value : null;
      const incoming = adminResult.status === "fulfilled" ? adminResult.value : null;
      const leadCount =
        leadsResult.status === "fulfilled" ? (leadsResult.value.leads ?? []).length : null;
      setLeads(leadCount);
      setOverview({
        version: incoming?.version ?? health?.version ?? "0.2.0",
        mode: incoming?.mode ?? health?.mode ?? state.snapshot.mode,
        esl: incoming?.esl ?? health?.esl,
        tts: incoming?.tts ?? health?.tts,
        db: incoming?.db ?? health?.db,
        sip: incoming?.sip ?? state.snapshot.sipText,
        setup: incoming?.setup ?? health?.setup ?? state.setup,
        leads_loaded: incoming?.leads_loaded ?? leadCount ?? undefined,
        paths: incoming?.paths,
        secrets: {
          has_fish: incoming?.secrets?.has_fish ?? incoming?.has_fish ?? settings?.has_fish_key,
          has_esl_password:
            incoming?.secrets?.has_esl_password ??
            incoming?.has_esl_password ??
            settings?.has_esl_password,
          has_bitcall: incoming?.secrets?.has_bitcall ?? incoming?.has_bitcall ?? settings?.has_bitcall,
          has_deepl: incoming?.secrets?.has_deepl ?? incoming?.has_deepl ?? settings?.has_deepl,
        },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [state.setup, state.snapshot.mode, state.snapshot.sipText]);

  const secrets = overview.secrets ?? {};
  const paths = overview.paths ?? {};
  const pathEntries = Object.entries(paths);

  return (
    <PageBody>
      <PageHeader
        kicker="System"
        title="Admin"
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate("/sip")}>
              SIP
            </Button>
            <Button variant="ghost" onClick={() => navigate("/settings")}>
              Settings
            </Button>
            <Button variant="danger" onClick={() => void logout()}>
              Sign out
            </Button>
          </>
        }
      />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile label="Version" value={overview.version || "—"} tone="teal" />
        <Tile label="Mode" value={String(overview.mode || "offline")} tone={overview.mode === "live" ? "live" : "mute"} />
        <Tile label="ESL" value={overview.esl ? "up" : "down"} tone={light(overview.esl)} />
        <Tile label="TTS" value={overview.tts ? "up" : "down"} tone={light(overview.tts)} />
        <Tile label="DB" value={overview.db ? "up" : "down"} tone={light(overview.db)} />
        <Tile
          label="SIP"
          value={sipStatusLabel(overview.sip, state.snapshot.sipText)}
          tone={light(state.snapshot.sip === "REGED" || state.snapshot.sip === "NOREG")}
        />
        <Tile label="Setup" value={overview.setup ? "ready" : "needed"} tone={light(overview.setup)} />
        <Tile
          label="Leads loaded"
          value={String(overview.leads_loaded ?? leads ?? 0)}
          tone="teal"
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <div className="mb-4 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
            Secrets configured
          </div>
          <ul className="space-y-2 font-mono text-sm">
            <li className="flex justify-between">
              <span className="text-mute">Fish</span>
              <StatusPill tone={light(secrets.has_fish)}>{secrets.has_fish ? "set" : "missing"}</StatusPill>
            </li>
            <li className="flex justify-between">
              <span className="text-mute">ESL password</span>
              <StatusPill tone={light(secrets.has_esl_password)}>
                {secrets.has_esl_password ? "set" : "missing"}
              </StatusPill>
            </li>
            <li className="flex justify-between">
              <span className="text-mute">Bitcall</span>
              <StatusPill tone={light(secrets.has_bitcall)}>
                {secrets.has_bitcall ? "set" : "missing"}
              </StatusPill>
            </li>
            <li className="flex justify-between">
              <span className="text-mute">DeepL</span>
              <StatusPill tone={light(secrets.has_deepl)}>{secrets.has_deepl ? "set" : "missing"}</StatusPill>
            </li>
          </ul>
        </Card>
        <Card className="p-5">
          <div className="mb-4 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
            Volume paths
          </div>
          {pathEntries.length === 0 ? (
            <p className="font-mono text-sm text-mute">Path flags unavailable — sibling overview not mounted.</p>
          ) : (
            <ul className="space-y-2 font-mono text-sm">
              {pathEntries.map(([name, exists]) => (
                <li key={name} className="flex justify-between">
                  <span className="text-mute">{name}</span>
                  <StatusPill tone={light(Boolean(exists))}>{exists ? "present" : "missing"}</StatusPill>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </PageBody>
  );
}
